import React, { useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';

/* Page-relevant wording, keyed by route. Falls back to the generic build message. */
const COPY = {
  // ---- Quality Assurance ----
  '/services/digital-assurance': ['Ready to certify releases in hours?', "Let's design the assurance layer your product deserves."],
  '/services/core-testing': ['Ready to make quality non-negotiable?', "Let's put essential assurance behind your next release."],
  '/services/advanced-assurance': ['Ready to scale your assurance?', 'Deeper coverage, automation-first. Tell us where it hurts.'],
  '/services/specialized-testing': ['Got a specialised testing challenge?', "Performance, security, accessibility — we'll take it on."],
  '/services/generative_ai_testing': ['Shipping AI features?', "Let's make sure your models behave in production."],
  // ---- Product Engineering ----
  '/services/product-engineering': ['Have a product to build?', "Bring the idea — we'll bring the engineering pod."],
  '/services/development-services': ['Need engineers who actually ship?', "Let's scope the build and get moving."],
  '/services/specialized-engineering': ['Got a hard engineering problem?', "The kind most teams route around — that's ours."],
  '/services/ai_engineering': ['Building something with AI?', "Let's turn the model into a product people actually use."],
  // ---- Digital Transformation ----
  '/services/digital-transformation': ['Ready to modernise how you deliver?', "Let's map the shortest path from legacy to velocity."],
  '/services/transformational-consulting': ['Need a transformation that sticks?', "Let's build the operating model, not just the deck."],
  '/services/strategic-assessments': ['Want an honest read on where you stand?', 'Start with an assessment. No theater, just findings.'],
  '/services/devops': ['Ready to deploy without drama?', "Let's build the pipeline your team deserves."],
  '/services/staffing-consulting': ['Need senior people, fast?', "Let's talk about the team you're missing."],
};

const ContactButton = ({ title, subtitle }) => {
  const location = useLocation();
  const rootRef = useRef(null);
  const path = (location.pathname || '').replace(/\/+$/, '') || '/';
  const [fallbackTitle, fallbackSub] = COPY[path] || ['Have something to build?', "Bring the sketch. We'll bring the velocity."];
  const heading = title || fallbackTitle;
  const sub = subtitle || fallbackSub;

  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) { en.target.classList.add('on'); io.unobserve(en.target); } });
    }, { threshold: 0.2 });
    root.querySelectorAll('.fbcta-rv').forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);

  return (
    <section className="fbcta" ref={rootRef}>
      <style>{`
        .fbcta { position:relative; padding:118px 24px; text-align:center; overflow:hidden; background:#f4f6f9;
          font-family:'Poppins',sans-serif; }
        .fbcta video { position:absolute; left:-6%; top:-6%; width:112%; height:112%; max-width:none; object-fit:cover; opacity:.92; }
        .fbcta .fbcta-scrim { position:absolute; inset:0; pointer-events:none;
          background:radial-gradient(64% 62% at 50% 50%, rgba(255,255,255,.86), rgba(255,255,255,.45) 58%, transparent 84%),
                     linear-gradient(180deg, rgba(255,255,255,.55), transparent 24% 74%, rgba(255,255,255,.72)); }
        .fbcta .fbcta-inner { position:relative; z-index:2; max-width:780px; margin:0 auto; }
        .fbcta h2 { font-size:clamp(30px,4.8vw,54px); font-weight:800; color:#131a26; letter-spacing:-.02em; line-height:1.15;
          margin:0 0 16px; text-shadow:0 2px 20px rgba(255,255,255,.85); }
        .fbcta p { display:table; margin:0 auto 30px; color:#2a3340; font-size:clamp(15px,1.6vw,18px); font-weight:500;
          background:rgba(255,255,255,.78); backdrop-filter:blur(6px); -webkit-backdrop-filter:blur(6px);
          padding:10px 22px; border-radius:999px; border:1px solid rgba(255,255,255,.9);
          box-shadow:0 6px 24px rgba(19,26,38,.06); }
        .fbcta a.fbcta-btn { display:inline-flex; align-items:center; gap:10px; background:#F93400; color:#fff; text-decoration:none;
          font-weight:600; font-size:15px; padding:15px 32px; border-radius:999px; border:1px solid #F93400;
          box-shadow:0 14px 40px rgba(249,52,0,.36);
          transition:transform .25s cubic-bezier(.34,1.56,.64,1), background .2s, box-shadow .25s; }
        .fbcta a.fbcta-btn:hover { background:#D93800; border-color:#D93800; transform:translateY(-3px) scale(1.03); color:#fff; }
        .fbcta a.fbcta-btn .fbcta-arr { transition:transform .25s; }
        .fbcta a.fbcta-btn:hover .fbcta-arr { transform:translateX(5px); }
        .fbcta .fbcta-rv { opacity:0; transform:translateY(26px); transition:opacity .8s ease, transform .8s cubic-bezier(.16,.84,.32,1); }
        .fbcta .fbcta-rv.on { opacity:1; transform:none; }
        @media (prefers-reduced-motion: reduce) {
          .fbcta video { display:none; }
          .fbcta .fbcta-rv { opacity:1 !important; transform:none !important; }
        }
      `}</style>
      <video muted loop playsInline autoPlay preload="none" src="/nh-media/cta-tech.mp4" />
      <div className="fbcta-scrim" />
      <div className="fbcta-inner">
        <h2 className="fbcta-rv">{heading}</h2>
        <p className="fbcta-rv">{sub}</p>
        <Link className="fbcta-btn fbcta-rv" to="/contact-us">Talk to Firebucks <span className="fbcta-arr">→</span></Link>
      </div>
    </section>
  );
};

export default ContactButton;
